/**
 * Fetching, decoding and keeping audio around.
 *
 * Three caches with very different lifetimes sit here:
 *
 *   * The **byte cache** is the Cache API. It survives restarts, it is what the storage
 *     budget in settings actually measures, and it is what makes a recording "available
 *     offline". Eviction is the user's business, so it is explicit and reported.
 *   * The **session download** holds compressed parts in memory regardless of the disk
 *     allowance. The 2 November 2026 locked-screen sit lost its network at the next
 *     just-in-time fetch, 588 s in; persistent caching alone cannot protect that sit.
 *   * The **decode cache** is in memory and tiny on purpose. A decoded minute of speech
 *     is 10 MB where its Opus is 240 KB, so holding more than the two islands either
 *     side of the playhead is how you get killed on mobile Safari. Measured budget for a
 *     60-minute sit with lazy residency is 13.7 MB against 330 MB for pre-rendering.
 */

import type { IslandPart, Recording, SessionPlan } from '../types';
import { islandParts } from './parts';
import { scheduleOps } from './schedule';
import { AudioFetchError, SessionDownload, type DownloadEvent } from './session-download';
export { retryDelayMs } from './session-download';

export { islandParts };
import { ToneProfile } from './tone';

export const AUDIO_CACHE = 'brahm-audio-v1';

import type { Container } from './containers';
export type { Container };

export interface SourceConfig {
  assetBase: string;
  /** Filled in from a decode test at startup — feature flags are not trustworthy here. */
  containers: Container[];
  /** Bytes the user has allowed us to keep. Zero means stream-only. */
  storageBudgetBytes: number;
}

// ---------------------------------------------------------------- capability

/**
 * Find out what this browser can really decode, by decoding something.
 *
 * `canPlayType` and `MediaSource.isTypeSupported` both disagree with reality often
 * enough that the answer has to be measured. The cost is four tiny fetches, once.
 */
export async function detectContainers(base = ''): Promise<Container[]> {
  const probes: [Container, string][] = [
    ['opus', `${base}/probe-assets/opus.ogg`],
    ['webm', `${base}/probe-assets/opus.webm`],
    ['m4a', `${base}/probe-assets/aac.m4a`],
  ];
  const ctx = new OfflineAudioContext(1, 1024, 48000);
  const ok: Container[] = [];
  await Promise.all(probes.map(async ([name, url]) => {
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      await ctx.decodeAudioData(await res.arrayBuffer());
      ok.push(name);
    } catch {
      /* this container is simply not available here */
    }
  }));
  // Preserve preference order rather than completion order.
  return (['opus', 'webm', 'm4a'] as Container[]).filter((c) => ok.includes(c));
}

// ---------------------------------------------------------------- byte cache

export interface CachedSize { entries: number; bytes: number }

const CACHE_BYTES = 'x-brahm-byte-length';
const CACHE_ACCESSED = 'x-brahm-last-access';

/** Cache API has no transactions. Keep admission and eviction in one critical section. */
let cacheMutation = Promise.resolve();
const pinned = new Map<string, number>();

async function serializeCacheMutation<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks) return locks.request('brahm-audio-cache', fn);
  const previous = cacheMutation;
  let release!: () => void;
  cacheMutation = new Promise<void>((resolve) => { release = resolve; });
  await previous;
  try { return await fn(); } finally { release(); }
}

export class ByteStore {
  private readonly cacheName: string;

  // Plain field assignment rather than a parameter property: this module is reached from
  // src/audio/schedule.ts, which Node .mjs tools import under type-stripping, and
  // strip-only mode cannot rewrite parameter properties.
  constructor(cacheName = AUDIO_CACHE) {
    this.cacheName = cacheName;
  }

  private async cache(): Promise<Cache | null> {
    if (typeof caches === 'undefined') return null;
    try { return await caches.open(this.cacheName); } catch { return null; }
  }

  async get(url: string): Promise<ArrayBuffer | null> {
    return serializeCacheMutation(async () => {
      const c = await this.cache();
      const hit = await c?.match(url);
      if (!hit || !c) return null;
      const bytes = await hit.arrayBuffer();
      const headers = new Headers(hit.headers);
      headers.set(CACHE_BYTES, String(bytes.byteLength));
      headers.set(CACHE_ACCESSED, String(Date.now()));
      try {
        await c.put(url, new Response(bytes.slice(0), {
          status: hit.status, statusText: hit.statusText, headers,
        }));
      } catch { /* an access remains usable even when refreshing metadata fails */ }
      return bytes;
    });
  }

  pin(url: string): void {
    const key = `${this.cacheName}\n${url}`;
    pinned.set(key, (pinned.get(key) ?? 0) + 1);
  }

  unpin(url: string): void {
    const key = `${this.cacheName}\n${url}`;
    const count = pinned.get(key) ?? 0;
    if (count <= 1) pinned.delete(key); else pinned.set(key, count - 1);
  }

  /** Fetch, storing the bytes only if there is room in the user's budget. */
  async fetch(url: string, budgetBytes: number, signal?: AbortSignal): Promise<ArrayBuffer> {
    const cached = await this.get(url);
    if (cached) return cached;

    let res: Response;
    let buf: ArrayBuffer;
    try {
      res = await fetch(url, { signal });
      if (!res.ok) throw new AudioFetchError(url, res.status,
        res.headers.get('x-brahm-offline') === '1');
      buf = await res.arrayBuffer();
    } catch (error) {
      if (signal?.aborted || error instanceof AudioFetchError) throw error;
      throw new AudioFetchError(url);
    }

    const c = await this.cache();
    if (c && budgetBytes > 0) await serializeCacheMutation(async () => {
      const { bytes } = await this.sizeFrom(c);
      if (bytes + buf.byteLength > budgetBytes) return;
      try {
        await c.put(url, new Response(buf.slice(0), { headers: {
          'content-type': res.headers.get('content-type') ?? 'audio/ogg',
          [CACHE_BYTES]: String(buf.byteLength),
          [CACHE_ACCESSED]: String(Date.now()),
        } }));
      } catch { /* downloaded bytes remain usable; only offline retention was lost */ }
    });
    return buf;
  }

  async size(): Promise<CachedSize> {
    return serializeCacheMutation(async () => {
      const c = await this.cache();
      if (!c) return { entries: 0, bytes: 0 };
      return this.sizeFrom(c);
    });
  }

  private async sizeFrom(c: Cache): Promise<CachedSize> {
    const keys = await c.keys();
    let bytes = 0;
    await Promise.all(keys.map(async (k) => {
      const r = await c.match(k);
      if (!r) return;
      const raw = r.headers.get(CACHE_BYTES) ?? r.headers.get('content-length');
      const parsed = raw == null ? NaN : Number(raw);
      if (Number.isSafeInteger(parsed) && parsed >= 0) {
        bytes += parsed;
        return;
      }
      // Entries written by older builds have no metadata. Measure the body once, then
      // replace the response with the same bytes and validated accounting headers.
      const body = await r.arrayBuffer();
      bytes += body.byteLength;
      const headers = new Headers(r.headers);
      headers.set(CACHE_BYTES, String(body.byteLength));
      headers.set(CACHE_ACCESSED, String(Date.now()));
      try { await c.put(k, new Response(body, { status: r.status, statusText: r.statusText, headers })); }
      catch { /* reconciliation is opportunistic; the measured answer is still correct */ }
    }));
    return { entries: keys.length, bytes };
  }

  async evict(urls: string[]): Promise<number> {
    const c = await this.cache();
    if (!c) return 0;
    let n = 0;
    for (const u of urls) if (await c.delete(u)) n++;
    return n;
  }

  async clear(): Promise<void> {
    await serializeCacheMutation(async () => {
      if (typeof caches !== 'undefined') await caches.delete(this.cacheName);
    });
  }

  /** Apply a reduced allowance now instead of waiting for a later download. */
  async trim(budgetBytes: number, pinnedUrls: ReadonlySet<string> = new Set()): Promise<void> {
    await serializeCacheMutation(async () => {
      const c = await this.cache();
      if (!c) return;
      const candidates = await Promise.all((await c.keys()).map(async (request) => {
        const response = await c.match(request);
        const raw = response?.headers.get(CACHE_BYTES) ?? response?.headers.get('content-length');
        const parsed = raw == null ? NaN : Number(raw);
        const bytes = Number.isSafeInteger(parsed) && parsed >= 0
          ? parsed : response ? (await response.clone().blob()).size : 0;
        const accessed = Number(response?.headers.get(CACHE_ACCESSED) ?? 0);
        return { request, bytes, accessed };
      }));
      let total = candidates.reduce((sum, item) => sum + item.bytes, 0);
      candidates.sort((a, b) => a.accessed - b.accessed || a.request.url.localeCompare(b.request.url));
      for (const item of candidates) {
        if (total <= budgetBytes) break;
        if (pinnedUrls.has(item.request.url)
            || pinned.has(`${this.cacheName}\n${item.request.url}`)) continue;
        if (await c.delete(item.request)) total -= item.bytes;
      }
    });
  }

  /** Which recordings are fully present, so "available offline" can be honest. */
  async residentRecordings(recs: Recording[], cfg: SourceConfig): Promise<Set<string>> {
    const c = await this.cache();
    if (!c) return new Set();
    const present = new Set((await c.keys()).map((r) => new URL(r.url).pathname));
    const out = new Set<string>();
    for (const rec of recs) {
      const urls = islandUrls(rec, cfg);
      if (urls.length && urls.every((u) => present.has(new URL(u, location.origin).pathname))) {
        out.add(rec.id);
      }
    }
    return out;
  }
}

// ---------------------------------------------------------------- urls

function urlFor(part: IslandPart, cfg: SourceConfig): string | null {
  for (const c of cfg.containers) {
    const f = part.files?.[c];
    if (f) return `${cfg.assetBase}/${f.path}`;
  }
  return null;
}

export function partUrl(
  rec: Recording, index: number, part: number, cfg: SourceConfig,
): string | null {
  const p = islandParts(rec, index)[part];
  return p ? urlFor(p, cfg) : null;
}

/** The first part's URL. Kept because callers that only need "is there audio" use it. */
export function islandUrl(rec: Recording, index: number, cfg: SourceConfig): string | null {
  return partUrl(rec, index, 0, cfg);
}

export function islandUrls(rec: Recording, cfg: SourceConfig): string[] {
  const out: string[] = [];
  for (let i = 0; i < rec.speech.length; i++) {
    for (const p of islandParts(rec, i)) {
      const u = urlFor(p, cfg);
      if (u) out.push(u);
    }
  }
  return out;
}

/** Derive residency from what will actually sound, including both sides of cue exclusions. */
function sessionAssets(rec: Recording, plan: SessionPlan, cfg: SourceConfig) {
  const assets = new Map<string, { url: string; bytes: number }>();
  for (const op of scheduleOps(rec, plan)) {
    if (op.prov.kind !== 'native') continue;
    const { island, part } = op.prov;
    const url = partUrl(rec, island, part, cfg);
    if (!url) throw new Error(`No audio asset for ${rec.id}#${island}.${part}`);
    const files = islandParts(rec, island)[part].files;
    const file = cfg.containers.map((container) => files?.[container]).find(Boolean);
    if (!assets.has(url)) assets.set(url, { url, bytes: file?.bytes ?? 0 });
  }
  return [...assets.values()];
}

export function sessionPartUrls(rec: Recording, plan: SessionPlan, cfg: SourceConfig): string[] {
  return sessionAssets(rec, plan, cfg).map((asset) => asset.url);
}

export function islandBytes(rec: Recording, cfg: SourceConfig): number {
  let total = 0;
  for (let i = 0; i < rec.speech.length; i++) {
    for (const p of islandParts(rec, i)) {
      for (const c of cfg.containers) {
        const f = p.files?.[c];
        if (f) { total += f.bytes; break; }
      }
    }
  }
  return total;
}

// ---------------------------------------------------------------- decode cache

/**
 * Decoded speech, bounded by total sample count rather than entry count — islands
 * range from four seconds to forty minutes and counting them equally would not bound
 * anything.
 */
export class DecodeCache {
  private readonly map = new Map<string, AudioBuffer>();
  private samples = 0;

  private readonly maxSamples: number;

  constructor(maxSamples: number) {
    this.maxSamples = maxSamples;
  }

  get(key: string): AudioBuffer | undefined {
    const hit = this.map.get(key);
    if (hit) { this.map.delete(key); this.map.set(key, hit); }   // refresh LRU position
    return hit;
  }

  set(key: string, buf: AudioBuffer): void {
    if (this.map.has(key)) this.samples -= this.map.get(key)!.length;
    this.map.set(key, buf);
    this.samples += buf.length;
    while (this.samples > this.maxSamples && this.map.size > 1) {
      const oldest = this.map.keys().next().value as string;
      this.samples -= this.map.get(oldest)!.length;
      this.map.delete(oldest);
    }
  }

  get residentSamples(): number { return this.samples; }
  clear(): void { this.map.clear(); this.samples = 0; }
}

// ---------------------------------------------------------------- loader

export class SourceLoader {
  private readonly profiles = new Map<string, Promise<ToneProfile>>();

  private readonly ctx: BaseAudioContext;
  private readonly store: ByteStore;
  private readonly cfg: SourceConfig;
  private readonly decoded: DecodeCache;
  private readonly pinnedUrls = new Set<string>();
  private session: SessionDownload | null = null;
  private sessionPlan: SessionPlan | null = null;

  startSession(rec: Recording, plan: SessionPlan, emit: (event: DownloadEvent) => void): void {
    if (this.sessionPlan === plan) return;
    this.endSession();
    this.session = new SessionDownload(sessionAssets(rec, plan, this.cfg),
      (url, signal) => this.store.fetch(url, this.cfg.storageBudgetBytes, signal), emit);
    this.sessionPlan = plan;
  }

  endSession(): void {
    this.session?.dispose();
    this.session = null;
    this.sessionPlan = null;
  }

  // Explicit assignment, for the same type-stripping reason as ByteStore above.
  constructor(
    ctx: BaseAudioContext,
    store: ByteStore,
    cfg: SourceConfig,
    // ~90 s of 48 kHz mono: the two islands either side of the playhead.
    decoded = new DecodeCache(90 * 48000),
  ) {
    this.ctx = ctx;
    this.store = store;
    this.cfg = cfg;
    this.decoded = decoded;
  }

  /** One decoded part. This is the only thing that ever becomes an AudioBuffer. */
  async part(rec: Recording, index: number, part = 0,
    onWaiting?: (url: string) => void): Promise<AudioBuffer> {
    const key = `${rec.id}#${index}.${part}`;
    const hit = this.decoded.get(key);
    if (hit) return hit;

    const url = partUrl(rec, index, part, this.cfg);
    if (!url) throw new Error(`no audio for ${key} in ${this.cfg.containers.join('/')}`);

    const session = this.session;
    const bytes = session?.has(url)
      ? await session.read(url, onWaiting)
      : await this.store.fetch(url, this.cfg.storageBudgetBytes);
    if (session && session !== this.session) throw new DOMException('Session ended', 'AbortError');
    if (!this.pinnedUrls.has(url)) { this.store.pin(url); this.pinnedUrls.add(url); }
    const buf = await this.ctx.decodeAudioData(bytes.slice(0));
    this.decoded.set(key, buf);
    return buf;
  }

  /** Back-compatible single-part accessor. */
  async island(rec: Recording, index: number): Promise<AudioBuffer> {
    return this.part(rec, index, 0);
  }

  /**
   * Warm the byte cache without paying to decode. Used to stay ahead of the playhead.
   * `limit` caps how many parts are pulled: a long island is tens of megabytes and only
   * its first part is needed soon.
   */
  async prefetchIsland(rec: Recording, index: number, limit = Infinity): Promise<void> {
    const n = Math.min(islandParts(rec, index).length, limit);
    for (let p = 0; p < n; p++) {
      const url = partUrl(rec, index, p, this.cfg);
      if (!url) continue;
      try { await this.store.fetch(url, this.cfg.storageBudgetBytes); } catch { /* best effort */ }
    }
  }

  async profile(rec: Recording): Promise<ToneProfile | null> {
    if (!rec.toneProfile) return null;
    const url = `${this.cfg.assetBase}/${rec.toneProfile}`;
    let p = this.profiles.get(url);
    if (!p) {
      p = this.store.fetch(url, this.cfg.storageBudgetBytes).then(ToneProfile.fromBytes);
      if (!this.pinnedUrls.has(url)) { this.store.pin(url); this.pinnedUrls.add(url); }
      this.profiles.set(url, p);
    }
    return p;
  }

  get residentSamples(): number { return this.decoded.residentSamples; }
  releaseDecoded(): void {
    this.decoded.clear();
    for (const url of this.pinnedUrls) this.store.unpin(url);
    this.pinnedUrls.clear();
  }
}
